import { getWarningDescriptionKey, getWarningFixKey, getWarningProblemKey, getWarningTitleKey } from "./warningKeys.js";
import { isReservedI18nextOptionKey } from "./reservedKeys.js";
import type { Warning } from "../schemas/warning/warning.js";

export type TranslatableWarning = Partial<Omit<Warning, "warningType">> & {
    // Explicitly acknowledging that warning type may no longer exist as a config, since we don't know
    // when this warning was generated.
    warningType: string;

    // Warning title isn't optional
    warningTitle: string;
};

export type TranslatedWarningProblem = {
    listKey: string;
    problemKey: string;
    text: string;
};

export type TranslatedWarningFix = {
    listKey: string;
    fixKey: string;
    text: string;
};

export type TranslatedWarning = {
    title: string;
    description: string;
    problems: TranslatedWarningProblem[];
    fixes: TranslatedWarningFix[];
};

export type WarningTranslateFn = (
    key: string,
    options?: { defaultValue?: string; replace?: Record<string, string | number> },
) => string;

function stripReservedKeys(
    params: Record<string, string | number> | undefined,
): Record<string, string | number> | undefined {
    if (!params) return params;
    return Object.fromEntries(Object.entries(params).filter(([k]) => !isReservedI18nextOptionKey(k)));
}

/**
 * Translates one keyed list (problems or fixes) into display entries. `listKey` is a
 * React-stable key made unique across duplicate (key, text) pairs by an occurrence
 * counter, so identical entries in one list don't collide.
 */
function translateKeyedList(
    items: { key: string; params?: Record<string, string | number>; defaultDisplayText?: string }[],
    getCatalogKey: (key: string) => string,
    t: WarningTranslateFn,
): { listKey: string; key: string; text: string }[] {
    const occurrenceCounts = new Map<string, number>();
    return items.map(({ key, params, defaultDisplayText }) => {
        const text = t(getCatalogKey(key), {
            defaultValue: defaultDisplayText ?? key,
            replace: stripReservedKeys(params),
        });
        const keyBase = `${key}:${text}`;
        const occurrence = (occurrenceCounts.get(keyBase) ?? 0) + 1;
        occurrenceCounts.set(keyBase, occurrence);
        return { listKey: `${keyBase}:${occurrence}`, key, text };
    });
}

/**
 * Translates a full warning payload into UI-ready title/description/problems/fixes.
 * This exists so dashboard, backend, and other clients share one canonical
 * warning translation path regardless of which i18n engine they use.
 * Pass the consumer's translator function (`t`) to apply locale-specific copy.
 */
export function translateWarning(warning: TranslatableWarning, t: WarningTranslateFn): TranslatedWarning {
    const safeParams = stripReservedKeys(warning.warningParams);
    const problems: TranslatedWarningProblem[] = translateKeyedList(
        (warning.warningProblems ?? []).map(({ problemKey, problemParams, defaultDisplayText }) => ({
            key: problemKey,
            params: problemParams,
            defaultDisplayText,
        })),
        (key) => getWarningProblemKey(warning.warningType, key),
        t,
    ).map(({ listKey, key, text }) => ({ listKey, problemKey: key, text }));

    const fixes: TranslatedWarningFix[] = translateKeyedList(
        (warning.warningFixes ?? []).map(({ fixKey, fixParams, defaultDisplayText }) => ({
            key: fixKey,
            params: fixParams,
            defaultDisplayText,
        })),
        (key) => getWarningFixKey(warning.warningType, key),
        t,
    ).map(({ listKey, key, text }) => ({ listKey, fixKey: key, text }));

    return {
        title: t(getWarningTitleKey(warning.warningType), {
            defaultValue: warning.warningTitle,
            replace: safeParams,
        }),
        description: t(getWarningDescriptionKey(warning.warningType, warning.warningVariant ?? undefined), {
            defaultValue: warning.warningDetails ?? "",
            replace: safeParams,
        }),
        problems,
        fixes,
    };
}
